import { useMemo, useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Button from "../../components/reusableElementsComponents/Button";
import {
  FormControlLabel,
  Checkbox,
  FormControl,
  TextField,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import EmailIcon from "@mui/icons-material/Email";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { inputLabelClasses } from "@mui/material/InputLabel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import loading from "../../assets/images/icons/loading.gif";
import chrome from "../../assets/images/icons/chrome.png";
import { logLogin } from "../../analytics";
import * as Constants from "../../constants";
import TalentsApiService from "../../TalentsApiService";
import { t } from "../../content";
import { useLocale } from "../../locale";
import AuthPageChrome from "../../components/layoutComponents/authComponents/AuthPageChrome";

function Login() {
  const navigate = useNavigate();
  const { withLocale } = useLocale();
  const location = useLocation();
  const inviteParams = Object.fromEntries(
    new URLSearchParams(location.search).entries()
  );

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: 4,
  };

  const talentsServiceObj = useMemo(() => {
    return new TalentsApiService();
  }, []);

  let userAgent = navigator.userAgent;

  useEffect(() => {
    if (!userAgent.match(/chrome|chromium|crios/i)) {
      setOpen(true);
    }
  }, [userAgent]);

  const defaultValues = {
    email: "",
    password: "",
    remember_me: false,
  };

  const defaultErrorState = {
    email: false,
    password: false,
  };

  const formErrorMessages = {
    email: t("validation.invalidEmailFormat"),
    password: t("validation.passwordInstruction"),
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [formErrors, setFormErrors] = useState(defaultErrorState);
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(false);

  const updateFormErrorsObject = (name, value) => {
    setFormErrors({
      ...formErrors,
      [name]: value,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormValues({
      ...formValues,
      [name]: value,
    });

    if (!value) {
      updateFormErrorsObject(name, true);
    }

    if (invalidCredentials) {
      setInvalidCredentials(false);
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    if (value !== undefined && value.length > 0) {
      validateFieldValues(name, value);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: checked,
    });
    validateFieldValues(name, checked);
  };

  const validateFieldValues = (name, value) => {
    let valid = false;
    switch (name) {
      case "email":
        valid = Constants.VALID_EMAIL.test(value);
        updateFormErrorsObject(name, !valid);
        if (!valid) {
          setError(true);
        }
        break;
      default:
        break;
    }
    return valid;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoader(true);
    let keys = Object.keys(formValues);
    let fieldErrors = {};
    for (let index = 0; index < keys.length; index++) {
      fieldErrors[keys[index]] = !validateFieldValues(
        keys[index],
        formValues[keys[index]]
      );
    }

    setFormErrors(fieldErrors);

    const loginPayload = { ...formValues };
    if (inviteParams.team) {
      loginPayload.hash = inviteParams.team;
      loginPayload.hash_type = "team";
    } else if (inviteParams.pool) {
      loginPayload.hash = inviteParams.pool;
      loginPayload.hash_type = "pool";
    }

    talentsServiceObj.Login(loginPayload).then((response) => {
      if (response.httpStatus === 200 && response.json.data) {
        logLogin();
        localStorage.setItem(
          Constants.MAZHR_JWT_KEY,
          JSON.stringify(response.json.data)
        );
        setLoader(false);
        navigate("/");
      } else {
        if (
          response.json?.error?.code === 1002 ||
          response.json?.error?.code === 1001
        ) {
          setInvalidCredentials(true);
        }
        setLoader(false);
      }
    });
  };

  return (
    <div className="auth-page-wrapper">
      <AuthPageChrome />
      <div className="form-wrapper">
        <a
          className="back-to-login-link bold reg-as-talent regular"
          href="https://corporate.apexteam.ai/login"
        >
          <p className="m-0 light">
            {"<< "}
            {t("auth.login.loginAsOrganization")}
          </p>
        </a>

        <div className="section">
          <h3 className="title semi-bold l mar-15 bottom">
            {t("auth.login.titleDesktop")}
          </h3>
          <form className="form" onSubmit={handleSubmit}>
            <FormControl fullWidth>
              <TextField
                InputLabelProps={{
                  sx: {
                    [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                  },
                }}
                placeholder={t("common.placeholders.startTyping")}
                id="email"
                label={t("common.fields.email")}
                name="email"
                type="text"
                autoComplete="on"
                value={formValues.email}
                helperText={formErrors.email && formErrorMessages.email}
                error={formErrors.email}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                variant="standard"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              <br />
              <TextField
                InputLabelProps={{
                  sx: {
                    [`&.${inputLabelClasses.shrink}`]: { color: "#979797" },
                  },
                }}
                id="password"
                placeholder={t("common.placeholders.startTyping")}
                label={t("common.fields.password")}
                name="password"
                type={passwordVisible ? "text" : "password"}
                autoComplete="on"
                value={formValues.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <button
                        type="button"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {passwordVisible ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </button>
                    </InputAdornment>
                  ),
                }}
                variant="standard"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              <br />
              <div className="rememberMeWrapper">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="remember_me"
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={t("auth.login.rememberMe")}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />
                <div className="recover-password">
                  <Link
                    to={withLocale("/recover-password")}
                    className="bold green"
                  >
                    {t("auth.login.recoverPassword")}
                  </Link>
                </div>
              </div>
              {invalidCredentials && (
                <Alert severity="error">
                  <AlertTitle>{t("validation.invalidCredentials")}</AlertTitle>
                </Alert>
              )}
            </FormControl>
            <Button
              type="submit"
              btnstyle={"mazhrBtn default-green white full-width"}
            >
              {loader && !error ? (
                <img className="loader" src={loading} alt={t("common.labels.loading")} />
              ) : (
                <p className="m-0 light">{t("auth.login.signIn")}</p>
              )}
            </Button>
          </form>
        </div>

        <div>
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                <p>
                  {t("auth.login.chromeRecommendBefore")}
                  <a
                    href="https://https://www.google.com/chrome/"
                    className="link"
                  >
                    Chrome
                  </a>
                  {t("auth.login.chromeRecommendAfter")}
                </p>
                <a
                  href="https://https://www.google.com/chrome/"
                  className="link"
                >
                  <img
                    style={{ width: "20px" }}
                    src={chrome}
                    alt="chrome"
                    className="me-2"
                  />{" "}
                  {t("auth.login.downloadChrome")}
                </a>
              </Typography>
            </Box>
          </Modal>
        </div>
      </div>
    </div>
  );
}

export default Login;
